import React, { useState, useEffect } from 'react';
import { Search, Filter, Plus, Edit3, Trash2, MapPin, Users, Home, BarChart3, AlertTriangle, CheckCircle, ArrowUpDown, Upload } from 'lucide-react';
import { propertyService } from '../../services/propertyService';
import { communeService } from '../../services/communeService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import CommuneFormModal from './components/CommuneFormModal';
import PropertyFormModal from './components/PropertyFormModal';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import DataIntegrityCheck from './components/DataIntegrityCheck';
import BulkOperations from './components/BulkOperations';

export default function IntegratedDataManagementCenter() {
  const [communes, setCommunes] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedCommune, setSelectedCommune] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  const [showCommuneModal, setShowCommuneModal] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showIntegrityCheck, setShowIntegrityCheck] = useState(false);
  const [showBulkOps, setShowBulkOps] = useState(false);
  
  const [editingCommune, setEditingCommune] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [communesResponse, propertiesResponse] = await Promise.all([
        communeService?.getCommunesWithPropertyCount(),
        propertyService?.getAllProperties()
      ]);
      
      // Extract data from service responses and handle errors
      const communesData = communesResponse?.data || [];
      const propertiesData = propertiesResponse?.data || [];
      
      // Handle service errors
      if (communesResponse?.error) {
        setError('Erreur lors du chargement des communes: ' + communesResponse?.error);
      }
      if (propertiesResponse?.error) {
        setError('Erreur lors du chargement des propriétés: ' + propertiesResponse?.error);
      }
      
      setCommunes(Array.isArray(communesData) ? communesData : []);
      setProperties(Array.isArray(propertiesData) ? propertiesData : []);
      
      if (communesData?.length > 0 && !selectedCommune) {
        setSelectedCommune(communesData?.[0]);
      }
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error('Error loading data:', err);
      setCommunes([]);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort communes
  const filteredCommunes = communes?.filter(commune => {
    const matchesSearch = commune?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         commune?.district?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    switch (filterType) {
      case 'high-density':
        return matchesSearch && (commune?.propertyCount || 0) > 5;
      case 'low-density':
        return matchesSearch && (commune?.propertyCount || 0) <= 2;
      case 'no-properties':
        return matchesSearch && (commune?.propertyCount || 0) === 0;
      case 'high-price':
        return matchesSearch && (commune?.average_price || 0) > 50000000;
      default:
        return matchesSearch;
    }
  })?.sort((a, b) => {
    let valueA, valueB;
    
    switch (sortBy) {
      case 'properties':
        valueA = a?.propertyCount || 0;
        valueB = b?.propertyCount || 0;
        break;
      case 'price':
        valueA = a?.average_price || 0;
        valueB = b?.average_price || 0;
        break;
      case 'population':
        valueA = a?.population || 0;
        valueB = b?.population || 0;
        break;
      case 'district':
        valueA = a?.district || '';
        valueB = b?.district || '';
        break;
      default:
        valueA = a?.name || '';
        valueB = b?.name || '';
    }
    
    if (sortOrder === 'asc') {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  }) || [];

  // Get properties for selected commune
  const selectedCommuneProperties = selectedCommune 
    ? properties?.filter(property => property?.commune_id === selectedCommune?.id) || []
    : [];

  // Handle commune operations
  const handleCommuneCreate = async (communeData) => {
    try {
      const response = await communeService?.createCommune(communeData);
      
      if (response?.error) {
        setError(response?.error);
        return;
      }
      
      setSuccess('Commune créée avec succès');
      setShowCommuneModal(false);
      await loadData();
    } catch (err) {
      console.error('Error creating commune:', err);
      setError('Erreur lors de la création de la commune');
    }
  };

  const handleCommuneUpdate = async (communeData) => {
    try {
      setError(''); // Clear any previous errors
      
      if (!editingCommune?.id) {
        setError('Erreur: Aucune commune sélectionnée pour la modification');
        return;
      }

      const response = await communeService?.updateCommune(editingCommune?.id, communeData);
      
      if (response?.error) {
        console.error('Error updating commune:', response?.error);
        setError(response?.error);
        return;
      }
      
      setSuccess('Commune mise à jour avec succès');
      setShowCommuneModal(false);
      setEditingCommune(null);
      await loadData();
    } catch (err) {
      console.error('Unexpected error updating commune:', err);
      setError('Erreur inattendue lors de la mise à jour de la commune');
    }
  };

  const handleCommuneDelete = async (communeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commune ?')) {
      return;
    }
    
    try {
      const response = await communeService?.deleteCommune(communeId);
      
      if (response?.error) {
        setError(response?.error);
        return;
      }
      
      setSuccess('Commune supprimée avec succès');
      await loadData();
      
      if (selectedCommune?.id === communeId) {
        setSelectedCommune(filteredCommunes?.[0] || null);
      }
    } catch (err) {
      console.error('Error deleting commune:', err);
      setError('Erreur lors de la suppression de la commune');
    }
  };

  // Handle property operations
  const handlePropertyCreate = async (propertyData) => {
    try {
      const response = await propertyService?.createProperty(propertyData);
      
      if (response?.error) {
        setError(response?.error);
        return;
      }
      
      setSuccess('Propriété créée avec succès');
      setShowPropertyModal(false);
      await loadData();
    } catch (err) {
      console.error('Error creating property:', err);
      setError('Erreur lors de la création de la propriété');
    }
  };

  const handlePropertyUpdate = async (propertyData) => {
    try {
      setError(''); // Clear any previous errors
      
      if (!editingProperty?.id) {
        setError('Erreur: Aucune propriété sélectionnée pour la modification');
        return;
      }

      const response = await propertyService?.updateProperty(editingProperty?.id, propertyData);
      
      if (response?.error) {
        console.error('Error updating property:', response?.error);
        setError(response?.error);
        return;
      }
      
      setSuccess('Propriété mise à jour avec succès');
      setShowPropertyModal(false);
      setEditingProperty(null);
      await loadData();
    } catch (err) {
      console.error('Unexpected error updating property:', err);
      setError('Erreur inattendue lors de la mise à jour de la propriété');
    }
  };

  const handlePropertyDelete = async (propertyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette propriété ?')) {
      return;
    }
    
    try {
      const response = await propertyService?.deleteProperty(propertyId);
      
      if (response?.error) {
        setError(response?.error);
        return;
      }
      
      setSuccess('Propriété supprimée avec succès');
      await loadData();
    } catch (err) {
      console.error('Error deleting property:', err);
      setError('Erreur lors de la suppression de la propriété');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    })?.format(price || 0);
  };

  const formatPopulation = (population) => {
    return new Intl.NumberFormat('fr-FR')?.format(population || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Centre de Gestion Intégrée</h1>
                <p className="text-gray-600">Gestion unifiée des communes et propriétés</p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  onClick={() => setShowAnalytics(true)}
                  variant="outline"
                  className="flex items-center"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytiques
                </Button>
                
                <Button
                  onClick={() => setShowIntegrityCheck(true)}
                  variant="outline"
                  className="flex items-center"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Intégrité
                </Button>
                
                <Button
                  onClick={() => setShowBulkOps(true)}
                  variant="outline"
                  className="flex items-center"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Opérations Groupées
                </Button>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Rechercher communes ou propriétés..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-10"
                />
              </div>

              <Select
                value={filterType}
                onChange={(e) => setFilterType(e?.target?.value)}
                className="w-48"
              >
                <option value="all">Tous les types</option>
                <option value="high-density">Forte densité (&gt;5 propriétés)</option>
                <option value="low-density">Faible densité (≤2 propriétés)</option>
                <option value="no-properties">Sans propriétés</option>
                <option value="high-price">Prix élevé (&gt;50M)</option>
              </Select>

              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="w-40"
              >
                <option value="name">Nom</option>
                <option value="district">District</option>
                <option value="properties">Propriétés</option>
                <option value="price">Prix moyen</option>
                <option value="population">Population</option>
              </Select>

              <Button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                variant="outline"
                className="flex items-center"
              >
                <ArrowUpDown className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Communes Panel */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow border">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                  Communes ({filteredCommunes?.length || 0})
                </h2>
                
                <Button
                  onClick={() => {
                    setEditingCommune(null);
                    setShowCommuneModal(true);
                  }}
                  className="flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {filteredCommunes?.map((commune) => (
                <div
                  key={commune?.id}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedCommune?.id === commune?.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                  }`}
                  onClick={() => setSelectedCommune(commune)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{commune?.name}</h3>
                      <p className="text-sm text-gray-600">{commune?.district}</p>
                      
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Home className="w-3 h-3 mr-1" />
                          {commune?.propertyCount || 0}
                        </span>
                        <span className="flex items-center">
                          <Users className="w-3 h-3 mr-1" />
                          {formatPopulation(commune?.population)}
                        </span>
                      </div>
                      
                      {commune?.average_price && (
                        <p className="text-sm font-medium text-green-600 mt-1">
                          {formatPrice(commune?.average_price)}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          setEditingCommune(commune);
                          setShowCommuneModal(true);
                        }}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <Edit3 className="w-4 h-4 text-gray-600" />
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleCommuneDelete(commune?.id);
                        }}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {filteredCommunes?.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Aucune commune trouvée</p>
                </div>
              )}
            </div>
          </div>

          {/* Properties Panel */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow border">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center">
                  <Home className="w-5 h-5 mr-2 text-green-600" />
                  Propriétés {selectedCommune && `- ${selectedCommune?.name}`} ({selectedCommuneProperties?.length || 0})
                </h2>
                
                {selectedCommune && (
                  <Button
                    onClick={() => {
                      setEditingProperty(null);
                      setShowPropertyModal(true);
                    }}
                    className="flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                )}
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {selectedCommuneProperties?.map((property) => (
                <div key={property?.id} className="p-4 border-b hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{property?.title}</h3>
                      <p className="text-sm text-gray-600">{property?.address}</p>
                      
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        <span className="capitalize bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {property?.property_type}
                        </span>
                        <span className={`px-2 py-1 rounded ${
                          property?.status === 'available' ? 'bg-green-100 text-green-800' :
                          property?.status === 'reserved' ? 'bg-yellow-100 text-yellow-800' :
                          property?.status === 'sold'? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {property?.status}
                        </span>
                        
                        {property?.bedrooms && (
                          <span>{property?.bedrooms} ch.</span>
                        )}
                        {property?.bathrooms && (
                          <span>{property?.bathrooms} sdb.</span>
                        )}
                      </div>
                      
                      <p className="text-sm font-medium text-green-600 mt-1">
                        {formatPrice(property?.price)}
                      </p>
                      
                      {property?.agent?.full_name && (
                        <p className="text-xs text-gray-500 mt-1">
                          Agent: {property?.agent?.full_name}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => {
                          setEditingProperty(property);
                          setShowPropertyModal(true);
                        }}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <Edit3 className="w-4 h-4 text-gray-600" />
                      </button>
                      
                      <button
                        onClick={() => handlePropertyDelete(property?.id)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {!selectedCommune && (
                <div className="p-8 text-center text-gray-500">
                  <Home className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Sélectionnez une commune pour voir ses propriétés</p>
                </div>
              )}
              
              {selectedCommune && selectedCommuneProperties?.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <Home className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Aucune propriété dans cette commune</p>
                  <Button
                    onClick={() => {
                      setEditingProperty(null);
                      setShowPropertyModal(true);
                    }}
                    className="mt-3"
                  >
                    Ajouter une propriété
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}

        {success && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
            <p className="text-green-700">{success}</p>
            <button
              onClick={() => setSuccess('')}
              className="ml-auto text-green-600 hover:text-green-800"
            >
              ×
            </button>
          </div>
        )}
      </div>
      {/* Modals */}
      {showCommuneModal && (
        <CommuneFormModal
          commune={editingCommune}
          onSave={editingCommune ? handleCommuneUpdate : handleCommuneCreate}
          onClose={() => {
            setShowCommuneModal(false);
            setEditingCommune(null);
          }}
        />
      )}
      {showPropertyModal && (
        <PropertyFormModal
          property={editingProperty}
          communes={communes}
          selectedCommuneId={selectedCommune?.id}
          onSave={editingProperty ? handlePropertyUpdate : handlePropertyCreate}
          onClose={() => {
            setShowPropertyModal(false);
            setEditingProperty(null);
          }}
        />
      )}
      {showAnalytics && (
        <AnalyticsDashboard
          communes={communes}
          properties={properties}
          onClose={() => setShowAnalytics(false)}
        />
      )}
      {showIntegrityCheck && (
        <DataIntegrityCheck
          communes={communes}
          properties={properties}
          onClose={() => setShowIntegrityCheck(false)}
          onRefresh={loadData}
        />
      )}
      {showBulkOps && (
        <BulkOperations
          communes={communes}
          properties={properties}
          onClose={() => setShowBulkOps(false)}
          onRefresh={loadData}
        />
      )}
    </div>
  );
}